import { Injectable } from '@angular/core'
import { createEffect, Actions, ofType } from '@ngrx/effects'
import { switchMap, catchError, tap, withLatestFrom, map, mergeMap } from 'rxjs'
import { LoaderActionTypeEnum } from 'libs/shared/utils/loader/src/lib/+state/loader.models'
import { GbLoaderService } from 'libs/shared/utils/loader/src/lib/services/gb-loader.service'
import { AuctionFacade } from 'libs/gb-front/auction/data-access/src/lib/+state/auction.facade'
import { SelectedItemApiService } from './selected-item-api.service'
import { AuthFacade } from 'libs/gb-front/auth/data-access/src/lib/+state/auth.facade'
import { SelectedProfileFacade } from 'libs/gb-front/selected-profile/data-access/src/lib/+state/selected-profile.facade'
import { SelectedItemFacade } from './selected-item.facade'
import { HttpErrorResponse } from '@angular/common/http'
import { GbDialogData } from 'libs/gb-front/shared/util/src/lib/gb-message-dialog/message-data.models'
import { TranslateService } from '@ngx-translate/core'
import { GbMessageDialogService } from 'libs/gb-front/shared/util/src/lib/gb-message-dialog/gb-message-dialog.service'
import { GbDisplayNamePipe } from 'libs/shared/utils/pipes/src/lib/gb-display-name-pipe/gb-display-name.pipe'
import { GbPaymentService } from 'libs/gb-front/payment/util/src/lib/gb-payment.service'
import { GbRaffleItemHelperService } from 'libs/gb-front/item/util/src/lib/gb-raffle-item-helper/gb-raffle-item-helper.service'
import * as SelectedItemActions from './selected-item.actions'
import { gbCheckLoaded } from 'libs/shared/utils/operators/src/lib/gb-check-loaded/gb-check-loaded.operator'
import { Action } from '@ngrx/store'
import { ProfileItemActivitiesFacade } from 'libs/gb-front/item/data-access-profile-item-activities/src/lib/+state/profile-item-activities.facade'
import { CreateRafflePurchaseResponse, RafflePurchasePayload } from './selected-item.models'
import { GbErrorMessageService } from 'libs/shared/utils/helpers/src/lib/gb-error-message/gb-error-message.service'

@Injectable()
export class SelectedItemRafflePurchaseEffects {
  checkRemainingRaffleUnits$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SelectedItemActions.checkRemainingRaffleUnits),
      gbCheckLoaded(
        this.profileItemActivitiesFacade.loaded$,
        () => this.loaderService.showLoaderDialog(LoaderActionTypeEnum.LoadProfileItemActivity, 'LOADING'),
        () => this.loaderService.hideLoaderDialog(LoaderActionTypeEnum.LoadProfileItemActivity),
      ),
      withLatestFrom(
        this.selectedItemFacade.currentAmount$,
        this.profileItemActivitiesFacade.remainingRaffleTicketsByProfile$,
      ),
      switchMap(([action, currentAmount, remainingTickets]) => {
        const actions: Action[] = []
        const purchaseLimitExceeded = currentAmount > remainingTickets

        if (!purchaseLimitExceeded) {
          actions.push(SelectedItemActions.openPaymentDialog())
        }

        return actions
      }),
    ),
  )

  placeRafflePurchase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SelectedItemActions.placeRafflePurchase),
      withLatestFrom(
        this.authFacade.authentication$,
        this.selectedProfileFacade.selectedProfileId$,
        this.selectedItemFacade.selectedItem$,
        this.auctionFacade.auctionUrlName$,
        this.selectedItemFacade.currentAmount$,
        this.auctionFacade.selectedPaymentMethod$,
        this.gbPaymentService.tippingFeeAmount$,
        this.gbRaffleItemHelper.selectedRafflePackage$,
        this.gbPaymentService.applyPaymentMethodFee$,
        this.gbPaymentService.donationAmount$,
      ),
      tap(() =>
        this.loaderService.showLoaderDialog(
          LoaderActionTypeEnum.PlaceRafflePurchase,
          'ITEM.RAFFLE.PLACING_RAFFLE_PURCHASE',
        ),
      ),
      mergeMap(
        ([
          ,
          authentication,
          selectedProfileId,
          item,
          urlName,
          amount,
          selectedPaymentMethod,
          tippingFeeAmount,
          selectedRafflePackage,
          applyPaymentMethodFee,
          donationAmount,
        ]) => {
          const rafflePurchasePayload: RafflePurchasePayload = {
            apply_fee: applyPaymentMethodFee,
            amount: amount,
            raffle_item: item?.raffle_item?.uuid,
            profile: selectedProfileId,
            payment_method: selectedPaymentMethod?.uuid,
            delegate: authentication.profile.profile,
            donation_amount: donationAmount,
          }

          if (tippingFeeAmount) {
            rafflePurchasePayload.tipping_fee = tippingFeeAmount
          }

          if (selectedRafflePackage) {
            rafflePurchasePayload.package = selectedRafflePackage.uuid
            // amount should always be '1' for raffle packages.
            // no. of tickets in selected package is handled by the API
            rafflePurchasePayload.amount = 1
          }

          return this.selectedItemApiService.createRafflePurchase(urlName, rafflePurchasePayload).pipe(
            map((response) => {
              const payload = {
                response,
                requiresPayment: true,
                payment: response.payment,
              }
              return SelectedItemActions.placeRafflePurchaseSuccess(payload)
            }),
            catchError(async (error: HttpErrorResponse) => SelectedItemActions.placeRafflePurchaseFailure({ error })),
          )
        },
      ),
    ),
  )

  showRafflePurchaseSuccessMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SelectedItemActions.finaliseRafflePurchaseSuccess),
        withLatestFrom(
          this.selectedProfileFacade.selectedProfile$,
          this.selectedItemFacade.selectedItem$,
          this.authFacade.isVolunteer$,
        ),
        switchMap(async ([, selectedProfile, item, isVolunteer]) => {
          let dialogPayload: GbDialogData = {
            title: this.translateService.instant('ITEM.RAFFLE.SUCCESS_MESSAGE_HEADER'),
            body: this.translateService.instant('ITEM.RAFFLE.SUCCESS_MESSAGE_DESCRIPTION', { item: item?.name }),
            activityFeed: true,
            messageType: 'success',
            item: item,
          }

          if (isVolunteer) {
            dialogPayload = {
              title: 'ITEM.RAFFLE.SUCCESS_MESSAGE_HEADER',
              body: this.translateService.instant('ITEM.RAFFLE.SUCCESS_MESSAGE_DESCRIPTION_VOLUNTEER', {
                user: this.gbDisplayNamePipe.transform(selectedProfile),
                item: item?.name,
              }),
              messageType: 'success',
              item: item,
            }
          }
          this.gbMessageDialogService.showDialog(dialogPayload)
        }),
      ),
    { dispatch: false },
  )

  placeRafflePurchaseFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SelectedItemActions.placeRafflePurchaseFailure),
        tap((action) => {
          const error = this.gbErrorMessageService.getErrorMessage(action.error.error)
          this.gbMessageDialogService.showDialog({
            title: 'ITEM.RAFFLE.ERROR_MESSAGE.TITLE',
            body: error || 'ITEM.RAFFLE.ERROR_MESSAGE.BODY',
            messageType: 'warning',
          })
        }),
      ),
    { dispatch: false },
  )

  hideRaffleLoader$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SelectedItemActions.placeRafflePurchaseSuccess, SelectedItemActions.placeRafflePurchaseFailure),
        map(() => this.loaderService.hideLoaderDialog(LoaderActionTypeEnum.PlaceRafflePurchase)),
      ),
    { dispatch: false },
  )

  constructor(
    private actions$: Actions,
    private selectedItemApiService: SelectedItemApiService,
    private selectedProfileFacade: SelectedProfileFacade,
    private selectedItemFacade: SelectedItemFacade,
    private authFacade: AuthFacade,
    private auctionFacade: AuctionFacade,
    private profileItemActivitiesFacade: ProfileItemActivitiesFacade,
    private loaderService: GbLoaderService,
    private gbRaffleItemHelper: GbRaffleItemHelperService,
    private gbMessageDialogService: GbMessageDialogService,
    private gbErrorMessageService: GbErrorMessageService,
    private gbPaymentService: GbPaymentService,
    private gbDisplayNamePipe: GbDisplayNamePipe,
    private translateService: TranslateService,
  ) {}
}
